// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { SignInForm } from '@medplum/react';
import type { CSSProperties, JSX, ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import polarisLogo from '../assets/polaris-logo.svg';
import { getConfig } from '../config';
import classes from './SignInPage.module.css';

const ICONS = {
  check: <path d="M20 6 9 17l-5-5" />,
  shield: <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3Z" />,
  help: (
    <g>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.2a2.6 2.6 0 0 1 5 .9c0 1.7-2.5 2.1-2.5 3.9" />
      <circle cx="12" cy="17.2" r="0.6" fill="currentColor" stroke="none" />
    </g>
  ),
};

const STARS = [
  [12, 22, 2.6, 0.9],
  [22, 14, 1.4, 0.5],
  [31, 30, 1.8, 0.65],
  [18, 46, 1.3, 0.45],
  [40, 20, 2.2, 0.8],
  [52, 33, 1.5, 0.5],
  [44, 52, 1.6, 0.55],
  [63, 18, 1.3, 0.45],
  [70, 40, 2.8, 1],
  [78, 26, 1.5, 0.5],
  [86, 48, 1.4, 0.45],
  [58, 66, 1.7, 0.55],
  [30, 70, 1.4, 0.4],
  [74, 72, 1.6, 0.5],
  [88, 12, 1.2, 0.4],
  [8, 62, 1.5, 0.45],
  [48, 80, 1.3, 0.4],
  [82, 84, 1.9, 0.6],
  [16, 84, 1.4, 0.42],
  [66, 88, 1.3, 0.4],
];

const LINES = [
  [12, 22, 40, 20],
  [40, 20, 70, 40],
  [70, 40, 52, 33],
  [70, 40, 78, 26],
  [31, 30, 12, 22],
];

const POINTS = [
  'Review sleep studies & test results',
  'Message your sleep care team',
  'Manage appointments and therapy',
];

function cx(...value: Array<string | false | null | undefined>): string {
  return value.filter(Boolean).join(' ');
}

interface IconProps {
  d: ReactNode;
  w?: number;
  sw?: number;
}

function Icon({ d, w = 18, sw = 1.7 }: IconProps): JSX.Element {
  return (
    <svg
      width={w}
      height={w}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {d}
    </svg>
  );
}

function Sky(): JSX.Element {
  return (
    <div className={cx(classes.sky, classes.show)}>
      {LINES.map((line, index) => {
        const dx = line[2] - line[0];
        const dy = line[3] - line[1];
        const len = Math.hypot(dx, dy);
        const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

        return (
          <div
            key={`line-${index}`}
            className={classes.skyline}
            style={{
              left: `${line[0]}%`,
              top: `${line[1]}%`,
              width: `${len}%`,
              opacity: 0.14,
              transform: `rotate(${angle}deg)`,
            }}
          />
        );
      })}

      {STARS.map((star, index) => (
        <div
          key={`star-${index}`}
          className={classes.star}
          style={{
            left: `${star[0]}%`,
            top: `${star[1]}%`,
            width: `${star[2] * 2}px`,
            height: `${star[2] * 2}px`,
            opacity: star[3] * 0.9,
            boxShadow: star[2] > 2.2 ? '0 0 8px 1px rgba(255,255,255,.6)' : 'none',
          }}
        />
      ))}
    </div>
  );
}

function Wordmark(): JSX.Element {
  return <span className={classes.wordmark} role="img" aria-label="Polaris" />;
}

function BrandPanel(): JSX.Element {
  return (
    <aside className={classes['auth-panel']}>
      <div className={classes['panel-grad']} />
      <Sky />

      <div className={classes['panel-mark']}>
        <Wordmark />
        <span className={classes['panel-tag']}>
          <span className={classes.pip} />
          Sleep
        </span>
      </div>

      <div className={classes['panel-hero']}>
        <h1>Your sleep care, all in one place.</h1>
        <p>
          Securely connect with your Polaris Sleep care team, track your therapy, and stay on top of every appointment.
        </p>
        <ul className={classes['panel-points']}>
          {POINTS.map((point) => (
            <li key={point}>
              <span className={classes.tick}>
                <Icon d={ICONS.check} w={13} sw={2.4} />
              </span>
              {point}
            </li>
          ))}
        </ul>
      </div>

      <div className={classes['panel-foot']}>
        <Icon d={ICONS.shield} w={15} sw={1.7} />
        HIPAA-compliant - Your information stays private &amp; encrypted
      </div>
    </aside>
  );
}

export function SignInPage(): JSX.Element {
  const accentVars = {
    '--maroon': '#640040',
    '--panel': '#640040',
  } as CSSProperties;
  const config = getConfig();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loginCode = searchParams.get('login') || undefined;

  const navigateTo = (path: string): void => {
    navigate(path);
  };

  return (
    <div className={classes['auth-root']} style={accentVars}>
      <BrandPanel />

      <main className={classes['auth-main']}>
        <div className={classes['auth-card']}>
          <div className={classes['medplum-signin']}>
            <SignInForm
              clientId={config.clientId}
              projectId={config.projectId}
              login={loginCode}
              onSuccess={() => navigateTo('/')}
              onForgotPassword={() => navigateTo('/forgot-password')}
              onRegister={() => navigateTo('/register')}
            >
              <div className={classes['center-brand']}>
                <img src={polarisLogo} alt="Polaris Sleep" />
              </div>
              <div className={classes['auth-head']}>
                <h2>Welcome back</h2>
                <p>Sign in to your Polaris Sleep patient account to continue.</p>
              </div>
            </SignInForm>
          </div>

          <div className={classes['secure-row']}>
            <span className={classes['secure-note']}>
              <Icon d={ICONS.shield} w={14} sw={1.8} />
              Protected by Medplum encrypted, HIPAA-compliant sign-in
            </span>
          </div>

          <div className={classes['auth-help']}>
            <Icon d={ICONS.help} w={15} sw={1.7} />
            <span>
              Trouble signing in? Email <a href="mailto:support@polarissleep.com">support@polarissleep.com</a>
            </span>
          </div>
        </div>
      </main>
    </div>
  );
}
